// LUMINA · pages/vajra/TeachingTopicPage.jsx
// Phase 68: Vajra Teachings Redesign — Theme Teachings List (单主题教法内页)

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API } from '../../constants/config';

const GD = "#D4AF37";
const BG = "#FDFBF7"; // Dark theme background
const TXT_DARK = "rgba(0,0,0,0.82)";
const TXT_MUTED = "rgba(0,0,0,0.50)";

const TYPE_LABELS_ZH = {
  empowerment:  "灌顶",
  teaching:     "教学",
  practice:     "实修",
  text_study:   "经典",
  retreat:      "闭关",
};

const TYPE_LABELS_EN = {
  empowerment:  "Empowerment",
  teaching:     "Teaching",
  practice:     "Practice",
  text_study:   "Scripture",
  retreat:      "Retreat",
};

const CREDENTIAL_LABELS_ZH = {
  "vajrakilaya":      "普巴金刚",
  "tara":             "度母",
  "simhamukha":       "作明佛母",
  "medicine_buddha":  "药师佛",
  "chenrezig":        "观世音",
  "vajrasattva":      "金刚萨埵",
  "manjushri":        "文殊菩萨",
  "amitabha":         "阿弥陀佛",
  "dzogchen_ngondro": "大圆满前行",
  "naropa_6yogas":    "那若六法",
  "mahamudra_ngondro":"大手印前行",
  "tara_practice":    "度母修法",
  "vajrakilaya_practice": "普巴金刚修法",
  "tsalung":          "扎龙脉气",
  "choying-dzod":     "法界宝藏论",
};

const CREDENTIAL_LABELS_EN = {
  "vajrakilaya":      "Vajrakilaya",
  "tara":             "Tara",
  "simhamukha":       "Simhamukha",
  "medicine_buddha":  "Medicine Buddha",
  "chenrezig":        "Chenrezig",
  "vajrasattva":      "Vajrasattva",
  "manjushri":        "Manjushri",
  "amitabha":         "Amitabha",
  "dzogchen_ngondro": "Dzogchen Ngondro",
  "naropa_6yogas":    "Naropa Six Yogas",
  "mahamudra_ngondro":"Mahamudra Ngondro",
  "tara_practice":    "Tara Practice",
  "vajrakilaya_practice": "Vajrakilaya Practice",
  "tsalung":          "Tsalung",
  "choying-dzod":     "Choying Dzod",
};

const T_TOPIC = {
  zh: {
    back: "返回法宝门厅",
    teacher: "主讲上师",
    allLocked: "此主题所有教法均需灌顶，请联系金刚社群进行资质审核。",
    loading: "正在开启法教通道...",
    error: "加载法教失败",
    noTeachings: "暂无相关教法",
    requiresEmpowerment: (label) => `🔒 需 [${label}] 灌顶资质`,
    prerequisite: "前置要求",
    materials: "参考资料",
    public: "公开",
    unlocked: "已解锁",
    stagesCount: (count) => `${count}个修行阶段`,
  },
  en: {
    back: "Back to Gateway",
    teacher: "Teacher",
    allLocked: "All teachings under this topic require empowerment. Please contact the Vajra Community.",
    loading: "Opening the gateway to teachings...",
    error: "Failed to load teachings",
    noTeachings: "No teachings available",
    requiresEmpowerment: (label) => `🔒 Requires [${label}] Empowerment`,
    prerequisite: "Prerequisites",
    materials: "Materials",
    public: "Public",
    unlocked: "Unlocked",
    stagesCount: (count) => `${count} practice stage${count > 1 ? "s" : ""}`,
  }
};

const getTraditionColors = (tradition, slug) => {
  const trad = tradition || "";
  if (slug === "yuthok-nyingtik") {
    return {
      primary: "#8B6914",
      gradient: "linear-gradient(135deg, #FFFDF5, #FDF0D5)",
      accent: "#D4AF37",
    };
  }
  if (slug === "vajrakilaya") {
    return {
      primary: "#0D9494",
      gradient: "linear-gradient(135deg, #FFF5F5, #F0F4FF)",
      accent: "#0D9494",
    };
  }
  if (trad.includes("宁玛")) {
    return {
      primary: "#8B1A1A",
      gradient: "linear-gradient(135deg, #FFF0F0, #F8E8E8)",
      accent: "#F56565",
    };
  }
  if (trad.includes("噶举")) {
    return {
      primary: "#1A5A8B",
      gradient: "linear-gradient(135deg, #F0F4FF, #E0ECFF)",
      accent: "#4299E1",
    };
  }
  if (trad.includes("格鲁")) {
    return {
      primary: "#D4AF37",
      gradient: "linear-gradient(135deg, #FFFDF5, #FDF0D5)",
      accent: "#ECC94B",
    };
  }
  if (trad.includes("觉囊")) {
    return {
      primary: "#8B4513",
      gradient: "linear-gradient(135deg, #FFF5F0, #FDF0E8)",
      accent: "#ED8936",
    };
  }
  if (trad.includes("萨迦")) {
    return {
      primary: "#5B7B8A",
      gradient: "linear-gradient(135deg, #F0F5FF, #E0ECF8)",
      accent: "#63B3ED",
    };
  }
  return {
    primary: "#6B4C3B",
    gradient: "linear-gradient(135deg, #F8F5F3, #F0EBE8)",
    accent: "#ED8936",
  };
};

export default function TeachingTopicPage({ lang = 'zh' }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [hub, setHub] = useState(null);
  const [teachings, setTeachings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);
  const token = localStorage.getItem("vajra_token") || "";

  const T = T_TOPIC[lang] || T_TOPIC.zh;

  useEffect(() => {
    window.scrollTo(0, 0);

    setLoading(true);
    // 1. Fetch Topic Hub Detail
    fetch(`${API}/vajra/topic-hubs/${slug}`)
      .then(r => {
        if (!r.ok) throw new Error(String(r.status));
        return r.json();
      })
      .then(hubData => {
        setHub(hubData);
        
        // 2. Fetch Teachings under this Topic
        const headers = token ? { "Authorization": `Bearer ${token}` } : {};
        return fetch(`${API}/vajra/teachings?topic_hub_slug=${slug}`, { headers });
      })
      .then(r => {
        if (!r.ok) throw new Error(String(r.status));
        return r.json();
      })
      .then(teachingsList => {
        setTeachings(teachingsList);
        setLoading(false);
      })
      .catch(err => {
        setError(T.error);
        setLoading(false);
      });
  }, [slug, token, lang]);

  const getCredLabel = (key) => {
    if (lang === "en") {
      return CREDENTIAL_LABELS_EN[key] || key;
    }
    return CREDENTIAL_LABELS_ZH[key] || key;
  };

  if (loading) {
    return (
      <div style={{
        minHeight: "100vh", background: BG, color: GD, display: "flex",
        flexDirection: "column", alignItems: "center", justifyContent: "center",
        fontFamily: "'Noto Serif SC', serif"
      }}>
        <div style={{ fontSize: 24, marginBottom: 16, animation: "pulse 2s infinite" }}>☸</div>
        <div style={{ fontSize: 14, letterSpacing: 2 }}>{T.loading}</div>
      </div>
    );
  }

  if (error || !hub) {
    return (
      <div style={{
        minHeight: "100vh", background: BG, color: "#C53030", display: "flex",
        alignItems: "center", justifyContent: "center", fontFamily: "'Noto Serif SC', serif"
      }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 28, marginBottom: 12 }}>⚠️</div>
          <div style={{ fontSize: 16 }}>{error || "专题未找到"}</div>
          <button onClick={() => navigate("/vajra/teachings")} style={{
            marginTop: 20, background: "none", border: `1px solid ${GD}`, color: GD,
            padding: "8px 16px", borderRadius: 4, cursor: "pointer"
          }}>
            {T.back}
          </button>
        </div>
      </div>
    );
  }

  const colors = getTraditionColors(hub.tradition, hub.slug);
  const allLockedAndNoToken = teachings.length > 0 && teachings.every(t => t.is_locked);

  return (
    <div style={{
      minHeight: "100vh", background: BG, color: TXT_DARK,
      fontFamily: "'Noto Serif SC', 'Noto Serif', serif", padding: "40px 24px 80px"
    }}>
      <div style={{ maxWidth: 960, margin: "0 auto" }}>
        {/* Breadcrumb */}
        <div style={{ marginBottom: 24, fontSize: 12, color: TXT_MUTED, fontFamily: "'Noto Sans SC', sans-serif" }}>
          <a href="/vajra" style={{ color: GD, textDecoration: "none" }}>{lang === "zh" ? "金刚社群" : "Vajra Community"}</a>
          {' › '}
          <a href="/vajra/teachings" style={{ color: GD, textDecoration: "none" }}>{lang === "zh" ? "法宝门厅" : "Teachings Gateway"}</a>
          {' › '}
          <span style={{ color: TXT_DARK }}>{lang === "en" && hub?.title_en ? hub.title_en : hub?.title_zh || slug}</span>
        </div>

        {/* Hero Area */}
        <div style={{
          background: colors.gradient,
          borderRadius: 16,
          border: `1px solid rgba(212,175,55,0.2)`,
          padding: "48px 36px",
          marginBottom: 40,
          position: "relative",
          overflow: "hidden",
          boxShadow: "0 4px 16px rgba(0,0,0,0.06)"
        }}>
          {/* Watermark/decoration */}
          <div style={{
            position: "absolute", right: -30, bottom: -30, fontSize: 180,
            color: "rgba(0,0,0,0.02)", fontFamily: "'Sanskrit', serif",
            userSelect: "none", pointerEvents: "none"
          }}>
            🪷
          </div>

          <div style={{ position: "relative", zIndex: 2 }}>
            <span style={{
              fontSize: 12, color: colors.accent, letterSpacing: 2,
              textTransform: "uppercase", fontWeight: 600
            }}>
              {hub.tradition || "通用传承"}
            </span>
            <h1 style={{
              fontSize: 32, fontWeight: 400, margin: "8px 0 4px 0",
              color: TXT_DARK, letterSpacing: 2
            }}>
              {lang === "en" && hub.title_en ? hub.title_en : hub.title_zh}
            </h1>
            {hub.title_en && lang !== "en" && (
              <div style={{ fontSize: 14, color: TXT_MUTED, fontStyle: "italic", marginBottom: 20 }}>
                {hub.title_en}
              </div>
            )}

            {/* Teacher info */}
            {hub.teacher_name_zh && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, fontSize: 14 }}>
                <span style={{ color: TXT_MUTED }}>{T.teacher}:</span>
                <span style={{ color: GD }}>🪷 {lang === "en" && hub.teacher_name_en ? hub.teacher_name_en : hub.teacher_name_zh}</span>
              </div>
            )}

            {/* Description */}
            {(lang === "en" ? hub.description_en : hub.description_zh) && (
              <p style={{
                fontSize: 14, color: "rgba(0,0,0,0.7)", lineHeight: "1.6",
                marginTop: 24, marginBottom: 0, borderTop: "1px solid rgba(0,0,0,0.08)",
                paddingTop: 16
              }}>
                {lang === "en" ? hub.description_en : hub.description_zh}
              </p>
            )}
          </div>
        </div>

        {/* All Locked Scenario Alert */}
        {allLockedAndNoToken && (
          <div style={{
            background: "rgba(139, 26, 26, 0.15)",
            border: "1px solid rgba(139, 26, 26, 0.3)",
            borderRadius: 8,
            padding: "16px 20px",
            marginBottom: 30,
            color: "#C53030",
            fontSize: 14,
            lineHeight: "1.5"
          }}>
            {T.allLocked}
          </div>
        )}

        {/* Teachings List */}
        {teachings.length === 0 ? (
          <div style={{ textAlign: "center", color: TXT_MUTED, fontSize: 14, padding: "40px 0" }}>
            {T.noTeachings}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {teachings.map(t => {
              const isLocked = t.is_locked;
              const isHovered = hoveredId === t.id;
              const typeLabel = lang === "en" ? (TYPE_LABELS_EN[t.teaching_type] || t.teaching_type) : (TYPE_LABELS_ZH[t.teaching_type] || t.teaching_type);

              return (
                <div
                  key={t.id}
                  onMouseEnter={() => !isLocked && setHoveredId(t.id)}
                  onMouseLeave={() => !isLocked && setHoveredId(null)}
                  onClick={() => {
                    if (!isLocked) {
                      navigate(`/teaching/${t.id}`);
                    }
                  }}
                  style={{
                    background: isLocked ? "rgba(0,0,0,0.02)" : "rgba(253,251,247,0.03)",
                    border: `1px solid ${isHovered ? "rgba(212,175,55,0.3)" : "rgba(0,0,0,0.04)"}`,
                    borderRadius: 10,
                    position: "relative",
                    overflow: "hidden",
                    display: "flex",
                    transition: "border-color 0.2s ease, transform 0.2s ease, background-color 0.2s ease",
                    transform: isHovered ? "translateX(4px)" : "none",
                    cursor: isLocked ? "default" : "pointer",
                    opacity: isLocked ? 0.5 : 1,
                  }}
                >
                  {/* Left tradition colored anchor bar */}
                  <div style={{
                    width: 4,
                    background: isLocked ? "rgba(255,255,255,0.15)" : colors.primary,
                    flexShrink: 0
                  }}></div>

                  {/* Body Content */}
                  <div style={{ padding: "24px 28px", flexGrow: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
                      {/* Type badge */}
                      {typeLabel && (
                        <span style={{
                          fontSize: 11,
                          background: isLocked ? "rgba(0,0,0,0.06)" : `rgba(${isHovered ? "212,175,55" : "255,255,255"}, 0.08)`,
                          color: isLocked ? "rgba(0,0,0,0.45)" : (isHovered ? GD : colors.accent),
                          border: `1px solid ${isLocked ? "rgba(0,0,0,0.1)" : (isHovered ? "rgba(212,175,55,0.2)" : "rgba(0,0,0,0.06)")}`,
                          padding: "2px 8px",
                          borderRadius: 4,
                          letterSpacing: 1,
                        }}>
                          {typeLabel}
                        </span>
                      )}

                      {/* Access / Locked message */}
                      {isLocked ? (
                        <span style={{ fontSize: 12, color: "#C53030", fontWeight: 500 }}>
                          {T.requiresEmpowerment(getCredLabel(t.credential_key))}
                        </span>
                      ) : (
                        t.credential_key && (
                          <span style={{ fontSize: 11, color: "#38A169", border: "1px solid rgba(104,211,145,0.2)", background: "rgba(56,161,105,0.06)", padding: "2px 8px", borderRadius: 4 }}>
                            {T.unlocked}
                          </span>
                        )
                      )}
                    </div>

                    {/* Title */}
                    <h3 style={{
                      fontSize: 18,
                      fontWeight: 400,
                      margin: "0 0 10px 0",
                      color: isHovered ? GD : TXT_DARK,
                      letterSpacing: 1,
                      transition: "color 0.2s ease"
                    }}>
                      {lang === "en" && t.title_en ? t.title_en : t.title_zh}
                    </h3>

                    {/* Description */}
                    {(lang === "en" ? t.description_en : t.description_zh) && (
                      <p style={{
                        fontSize: 13,
                        color: TXT_MUTED,
                        lineHeight: "1.5",
                        margin: "0 0 16px 0",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden"
                      }}>
                        {lang === "en" ? t.description_en : t.description_zh}
                      </p>
                    )}

                    {/* Footer Row */}
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      fontSize: 12,
                      color: TXT_MUTED,
                      borderTop: "1px solid rgba(0,0,0,0.06)",
                      paddingTop: 12,
                      flexWrap: "wrap",
                      gap: 12
                    }}>
                      {/* Teacher name */}
                      {t.teacher && (
                        <span>🪷 {lang === "en" && t.teacher.name_en ? t.teacher.name_en : t.teacher.name_zh}</span>
                      )}

                      {/* Stages or materials info */}
                      <div style={{ display: "flex", gap: 16 }}>
                        {t.stages && t.stages.length > 0 && (
                          <span>📄 {T.stagesCount(t.stages.length)}</span>
                        )}
                        {t.materials_note && (
                          <span>📁 {t.materials_note}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
